import { Component, inject, Inject, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NzButtonModule, NzButtonType } from 'ng-zorro-antd/button';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { BaseService } from '../../../core/services/base.service';
import { NotificationService } from '../../../core/services/notification.service';
import { TranslationService } from '../../../core/services/translation.service';
import { DBAction } from '../../enum/db-action.enum';
import { validateForms } from '../../utils/form.util';
import { TranslateService } from '@ngx-translate/core';

interface ModalButton {
  label: string;
  type: NzButtonType;
  action: () => void;
  position: 'left' | 'right';
}

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [NzSpinModule, NzButtonModule, NzModalModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export abstract class ModalComponent<T extends BaseService> implements OnInit {
  protected notificationService = inject(NotificationService);
  protected messageService = inject(NzMessageService);
  protected translate = inject(TranslateService);
  /** The active locale, for the `customCurrency`/`date` pipes in a subclass's template. */
  protected i18n = inject(TranslationService);

  readonly loading = signal(true);
  uuid: string | null = null;
  object: any | null = null;

  modalForm!: FormGroup;
  saving: boolean = false;

  constructor(
    @Inject(NZ_MODAL_DATA) data: any,
    protected modalRef: NzModalRef,
    @Inject(BaseService) protected service: T
  ) {
    this.uuid = data?.uuid || null;
  }

  get title() {
    if (!this.service) return 'Modal';
    const entity = this.translate.instant(this.service.dbName);
    return this.uuid ?
        this.translate.instant('edit', { entity })
      : this.translate.instant('add', { entity });
  }

  abstract setForm(): void;

  ngOnInit() {
    this.setForm();
    this.fetchData();
  }

  close() {
    this.modalForm?.reset();
    this.modalRef?.close();
  }

  // Fetch data based on uuid
  fetchData(): void {
    if (!this.uuid) {
      return;
    }

    this.loading.set(true);
    this.service?.getByUUID(this.uuid).subscribe({
      next: (resp: any) => {
        this.object = resp?.data ?? null;
        if (this.object) {
          this.modalForm.patchValue(this.object?.toForm());
          this.loading.set(false);
        } else {
          this.close();
        }
      },
      error: (err: any) => {
        this.close();
      },
    });
  }

  // Save form data
  save() {
    if (this.saving) return;

    if (this.modalForm.valid) {
      this.saving = true;
      const valueForm = this.modalForm.getRawValue();

      const request =
        this.uuid ? this.service?.update(this.uuid, valueForm) : this.service?.create(valueForm);

      const dbAction = this.uuid ? DBAction.UPDATE : DBAction.CREATE;

      request?.subscribe({
        next: (resp: any) => {
          this.notificationService?.successDB(this.service?.dbName || 'Modal', dbAction);
          this.saving = false;
          this.modalRef?.close({ success: true, data: resp?.data });
        },
        error: (err: any) => {
          this.saving = false;
        },
      });
    } else {
      validateForms(this.modalForm);
      this.messageService.error(this.translate.instant('notification.required_fields_error'));
    }
  }
}
